import React, { useEffect, useMemo, useRef, useState } from "react";

import { className } from "utils/strings";

import FALLBACK from "components/icons/image_icon.svg";
import "./Image.scss";

export const Image = (props: {
  className?: string;
  src: string | null | undefined;
  alt?: string | null | undefined;
  fallback?: string;
  onFailure?: (url: string) => void;
}): JSX.Element => {
  const [source, setSource] = useState<string|undefined>(undefined);
  const [imageAlt, setImageAlt] = useState<string|undefined>(undefined);
  const [isVisible, setVisible] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);
  const ref = useRef<HTMLImageElement>(null);

  const observer = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setSource(props.src ?? props.fallback ?? FALLBACK ?? undefined);
        setImageAlt(props.alt ?? props.fallback ?? FALLBACK ?? undefined);
        setVisible(true);
      }
    }, {
      rootMargin: "200px",
    });
  }, [props.src, props.alt, props.fallback]);

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [observer]);

  const onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.warn(`Error loading image ${e.currentTarget.src} -- showing fallback.`);
    props.onFailure && props.onFailure(e.currentTarget.src);
    e.currentTarget.src = FALLBACK;
  };

  const onLoad = () => {
    setReady(true);
  };

  const ready = isVisible && isReady;

  return (
    <img
      ref={ref}
      className={className({
        [props.className ?? ""]: true,
        lazyLoad: true,
        hidden: !ready,
        visible: !!ready,
      })}
      src={source}
      alt={imageAlt}
      onError={onError}
      onLoad={onLoad}
    />
  );
};
